import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { jobsAPI } from '../services/api';

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    job_type: '',
    work_mode: '',
    search: '',
    location: ''
  });

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      // Mapping local filters to API params
      const params = {
        job_type: filters.job_type,
        location: filters.location,
        search: filters.search
      };
      const response = await jobsAPI.getAll(params);
      setJobs(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load job listings');
      setLoading(false);
      console.error('Error fetching jobs:', err);
    }
  }, [filters.job_type, filters.location, filters.search]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const handleLocationChange = (e) => {
    setFilters(prev => ({ ...prev, location: e.target.value }));
  };

  const handleTypeChange = (type) => {
    setFilters(prev => ({ ...prev, job_type: type }));
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-16">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-on-surface-variant font-medium tracking-tight">Curating opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-on-background selection:bg-primary-fixed selection:text-on-primary-fixed min-h-screen pt-16">
      <div className="flex">
        {/* SideNavBar (Desktop) */}
        <aside className="h-screen w-64 hidden lg:flex flex-col fixed left-0 top-0 pt-24 px-4 space-y-6 bg-emerald-50 dark:bg-emerald-950 z-40">
          <div className="px-2 mb-4">
            <h2 className="text-lg font-black text-emerald-900 dark:text-emerald-50 font-['Inter']">Emerald Jobs</h2>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Local Opportunities</p>
          </div>
          <nav className="space-y-2 font-['Inter'] text-sm font-medium">
            <Link to="/jobs" className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-emerald-900 text-emerald-800 dark:text-emerald-100 rounded-xl shadow-sm translate-x-1 duration-150">
              <span className="material-symbols-outlined">dashboard</span>
              <span>Dashboard</span>
            </Link>
            <button className="w-full flex items-center gap-3 px-4 py-3 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-all text-left">
              <span className="material-symbols-outlined">bookmark</span>
              <span>Saved Jobs</span>
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-3 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-all text-left">
              <span className="material-symbols-outlined">work_history</span>
              <span>Applied</span>
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-3 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-all text-left">
              <span className="material-symbols-outlined">chat_bubble</span>
              <span>Messages</span>
            </button>
          </nav>
          <div className="pt-6">
            <button className="w-full py-4 bg-primary text-white rounded-full font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] transition-transform">
              Post a Job
            </button>
          </div>
        </aside>

        {/* Main Content Canvas */}
        <main className="flex-1 lg:ml-64 px-8 py-12 max-w-6xl mx-auto">
          {/* Hero Header */}
          <header className="mb-20 mt-8">
            <h1 className="text-[3.5rem] font-bold leading-[1.1] tracking-[-0.02em] text-on-background mb-6">
              Find Your Next<br/>Local <span className="text-primary italic">Opportunity</span>
            </h1>
            <p className="text-on-surface-variant text-lg max-w-2xl leading-relaxed">
              Connecting local talent with the community's most promising startups and established enterprises. High-quality roles curated daily by our talent advisors.
            </p>
          </header>

          {/* Main Search Bar */}
          <section className="relative z-10 -mt-10 mb-20">
            <div className="bg-surface-container-lowest p-4 rounded-full shadow-[0_32px_48px_rgba(18,28,42,0.06)] flex flex-col md:flex-row items-center gap-4 border border-outline-variant/15">
              <div className="flex-1 flex items-center gap-3 px-6 py-2">
                <span className="material-symbols-outlined text-on-surface-variant">search</span>
                <input 
                  className="w-full bg-transparent border-none focus:ring-0 text-on-surface placeholder:text-outline" 
                  placeholder="Job title, keywords, or company" 
                  type="text"
                  value={filters.search}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="hidden md:block w-px h-8 bg-outline-variant/20"></div>
              <div className="flex-1 flex items-center gap-3 px-6 py-2">
                <span className="material-symbols-outlined text-on-surface-variant">location_on</span>
                <input 
                  className="w-full bg-transparent border-none focus:ring-0 text-on-surface placeholder:text-outline" 
                  placeholder="City or neighborhood" 
                  type="text"
                  value={filters.location}
                  onChange={handleLocationChange}
                />
              </div>
              <button 
                onClick={fetchJobs}
                className="bg-primary hover:bg-primary-container text-white px-10 py-4 rounded-full font-bold transition-all shadow-lg shadow-primary/10"
              >
                Search Jobs
              </button>
            </div>
          </section>

          {/* Bento Layout Content */}
          <div className="grid grid-cols-12 gap-10">
            {/* Filter Sidebar */}
            <aside className="col-span-12 lg:col-span-3 space-y-10">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-outline mb-6">Job Type</h3>
                <div className="flex flex-wrap lg:flex-col gap-3">
                  {['Full-time', 'Part-time', 'Contract', 'Internship'].map((type) => (
                    <span 
                      key={type}
                      onClick={() => handleTypeChange(type)}
                      className={`${filters.job_type === type ? 'bg-primary-fixed text-on-primary-fixed' : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'} px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors`}
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-outline mb-6">Work Mode</h3>
                <div className="flex flex-wrap lg:flex-col gap-3">
                  {['On-site', 'Hybrid', 'Remote'].map((mode) => (
                    <label key={mode} className="flex items-center gap-3 cursor-pointer group" onClick={() => setFilters(prev => ({ ...prev, work_mode: mode }))}>
                      <div className={`w-5 h-5 rounded-md border-2 transition-colors flex items-center justify-center ${filters.work_mode === mode ? 'border-primary bg-primary' : 'border-outline group-hover:border-primary'}`}>
                        {filters.work_mode === mode && <span className="material-symbols-outlined text-[14px] text-white">check</span>}
                      </div>
                      <span className={`${filters.work_mode === mode ? 'text-on-surface font-semibold' : 'text-on-surface-variant'} text-sm`}>{mode}</span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Meet the Recruiter Module */}
              <div className="bg-surface-container-low p-8 rounded-[2rem] space-y-4">
                <div className="flex items-center gap-4">
                  <img alt="Sarah Jenkins" className="w-12 h-12 rounded-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuAeceAEAXxzk5917D5Zt5d0UVT0VU34HHGfghD7y1Lx-FIvOcU1gnBZD0CVSiRuZWW5mGV0X1XM9EsH8hcgXmL9kNFNJv9OXmABeQFEw2GaFm3HFDpYVS5l4H2oHclbi_-HqsLq_hxYzuPgNekC4XYcXnBcYamULWvuiR1XlofvafipQNyN_pEymfNNGSJHKMMEpQCeLbGBebwLjjvYH8AW5ESXvyVHS3h9ke3D-zJjc68h_RYO3DyK4QCiGRzMSGXYdNor6RL7VYA"/>
                  <div>
                    <h4 className="font-bold text-on-surface">Sarah Jenkins</h4>
                    <p className="text-xs text-on-surface-variant">Local Talent Advisor</p>
                  </div>
                </div>
                <p className="text-sm text-on-surface-variant leading-relaxed italic">
                  "Looking for something specific? I'm here to match you with opportunities that align with your career goals and community values."
                </p>
                <button className="w-full text-center py-3 text-sm font-bold text-primary border border-primary/20 rounded-full hover:bg-primary/5 transition-colors">
                  Message Sarah
                </button>
              </div>
            </aside>

            {/* Featured Opportunities */}
            <div className="col-span-12 lg:col-span-9 space-y-8">
              <div className="flex justify-between items-end mb-4">
                <h2 className="text-2xl font-bold tracking-tight">Featured Opportunities</h2>
                <span className="text-sm text-on-surface-variant font-medium">{jobs.length} roles found</span>
              </div>
              
              {error && (
                <div className="bg-error-container text-on-error-container p-6 rounded-[2rem]">
                  {error}
                </div>
              )}

              {jobs.length === 0 && !loading && !error && (
                <div className="text-center py-20 bg-surface-container-low rounded-[2.5rem] border border-dashed border-outline-variant/50">
                  <span className="material-symbols-outlined text-6xl text-on-surface-variant/30 mb-4">work_off</span>
                  <p className="text-on-surface-variant font-medium text-lg">No opportunities found matching your criteria.</p>
                  <button onClick={() => setFilters({ job_type: 'Full-time', work_mode: 'Hybrid', search: '', location: '' })} className="mt-6 text-primary font-bold hover:underline">Reset all filters</button>
                </div>
              )}

              {jobs.map((job) => (
                <article key={job.job_id} className="bg-surface-container-lowest p-8 rounded-[2rem] flex flex-col md:flex-row gap-8 items-start group hover:shadow-[0_20px_40px_rgba(18,28,42,0.04)] transition-all border border-outline-variant/5">
                  <div className="w-16 h-16 rounded-2xl bg-surface-container flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-3xl text-secondary">
                      {job.title.toLowerCase().includes('design') ? 'palette' : 
                       job.title.toLowerCase().includes('dev') ? 'code' : 
                       job.title.toLowerCase().includes('enviro') ? 'eco' : 'work'}
                    </span>
                  </div>
                  <div className="flex-1 space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <h3 className="text-xl font-bold text-on-surface group-hover:text-primary transition-colors">
                        {job.title}
                      </h3>
                      <span className="text-sm font-bold text-on-surface">
                        {job.salary_min && job.salary_max 
                          ? `$${(job.salary_min/1000).toFixed(0)}k – $${(job.salary_max/1000).toFixed(0)}k / yr` 
                          : 'Salary Undisclosed'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-on-surface-variant">
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[18px]">business</span>
                        <span>{job.company_name}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[18px]">location_on</span>
                        <span>{job.location_address}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[18px]">schedule</span>
                        <span>{job.job_type}</span>
                      </div>
                    </div>
                    <p className="text-on-surface-variant leading-relaxed line-clamp-2">
                      {job.description}
                    </p>
                  </div>
                  <div className="md:self-center">
                    <Link to={`/jobs/${job.job_id}`} className="px-6 py-3 rounded-full text-primary font-bold bg-primary-fixed/30 hover:bg-primary-fixed/50 transition-colors whitespace-nowrap inline-block">
                      View Details
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Floating Navigation (Mobile Only) */}
          <div className="md:hidden fixed bottom-6 left-6 right-6 bg-white shadow-2xl rounded-2xl py-4 px-8 flex justify-between items-center z-50 border border-outline-variant/10">
            <Link to="/explore"><span className="material-symbols-outlined text-emerald-400">explore</span></Link>
            <Link to="/"><span className="material-symbols-outlined text-emerald-400">home</span></Link>
            <Link to="/jobs"><span className="material-symbols-outlined text-emerald-900" style={{ fontVariationSettings: "'FILL' 1" }}>work</span></Link>
            <button><span className="material-symbols-outlined text-emerald-400">person</span></button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default JobsPage;
